import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

/**
 * Mode C — tracking the records an outgoing managing agent owes the management company.
 *
 * Never a threat and never a reason to withhold payment (the framing stays on the page).
 * "Received" is not "accepted": acceptance needs evidence or a written exception. Dates are the
 * ones the director records, and a due date is a diary reminder, not a deadline. The rules are
 * enforced by the API (handover.py); this page only offers what the item may do next.
 */

const STATE_LABEL = {
    not_requested: "Not requested",
    requested: "Requested",
    received: "Received — not yet checked",
    under_review: "Under review",
    accepted: "Accepted",
    needs_correction: "Needs correction",
    not_applicable: "Not applicable",
};

const STATE_TONE = {
    not_requested: "text-slate-500 border-slate-200",
    requested: "text-sky-800 border-sky-200",
    received: "text-amber-800 border-amber-200",
    under_review: "text-amber-800 border-amber-200",
    accepted: "text-emerald-800 border-emerald-200",
    needs_correction: "text-rose-800 border-rose-200",
    not_applicable: "text-slate-500 border-slate-200",
};

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY_EDIT = {
    state: "", owner: "", due_date: "", note: "", date: today(), na_reason: "", exception_reason: "", evidence: [],
};

export default function Handover() {
    const [checklist, setChecklist] = useState(null);
    const [engagements, setEngagements] = useState([]);
    const [engagementId, setEngagementId] = useState(null);
    const [view, setView] = useState(null);
    const [docs, setDocs] = useState([]);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);
    const [edit, setEdit] = useState(EMPTY_EDIT);

    const loadTracker = useCallback(async (id) => {
        if (!id) { setView(null); return; }
        try {
            const res = await api.get(`/agency-engagements/${id}/handover`);
            setView(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load the handover tracker");
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const [list, sheet, documents] = await Promise.all([
                    api.get("/agency-engagements"),
                    api.get("/agency-engagements/handover-checklist"),
                    api.get("/documents").catch(() => ({ data: [] })),
                ]);
                setEngagements(list.data || []);
                setChecklist(sheet.data);
                setDocs(documents.data || []);
                setEngagementId(sheet.data.engagement_id || (list.data && list.data[0]?.id) || null);
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not load handover");
                setChecklist({ items: [], framing: "", disclaimer: "" });
            }
        })();
    }, []);

    useEffect(() => { loadTracker(engagementId); }, [engagementId, loadTracker]);

    const docTitle = useMemo(() => Object.fromEntries(docs.map((d) => [d.id, d.title])), [docs]);

    const start = async () => {
        setBusy(true);
        try {
            const res = await api.post(`/agency-engagements/${engagementId}/handover/start`);
            setView(res.data);
            toast.success("Handover tracking started");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start tracking");
        } finally {
            setBusy(false);
        }
    };

    const openEdit = (item) => {
        setEditing(item);
        setEdit({
            ...EMPTY_EDIT, owner: item.owner || "", due_date: item.due_date || "", note: item.note || "",
            evidence: item.evidence_document_ids || [],
        });
    };

    const needsDate = edit.state === "requested" || edit.state === "received";
    const showEvidence = editing && ["received", "under_review", "accepted"].includes(edit.state || editing.state);

    const save = async () => {
        if (!editing) return;
        const body = { owner: edit.owner, due_date: edit.due_date || null };
        if (edit.state) body.state = edit.state;
        if (edit.state === "requested") body.requested_at = edit.date;
        if (edit.state === "received") body.received_at = edit.date;
        if (edit.state === "needs_correction") body.note = edit.note;
        else if (!edit.state) body.note = edit.note;
        if (edit.state === "not_applicable") body.na_reason = edit.na_reason;
        if (edit.state === "accepted") body.exception_reason = edit.exception_reason;
        if (showEvidence) body.evidence_document_ids = edit.evidence;
        setBusy(true);
        try {
            await api.patch(`/agency-engagements/${engagementId}/handover/items/${editing.id}`, body);
            setEditing(null);
            toast.success("Saved");
            await loadTracker(engagementId);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save that change");
        } finally {
            setBusy(false);
        }
    };

    const toggleEvidence = (id) => setEdit((e) => ({
        ...e, evidence: e.evidence.includes(id) ? e.evidence.filter((x) => x !== id) : [...e.evidence, id],
    }));

    if (!checklist) {
        return <div className="p-8 text-sm text-slate-500" data-testid="handover-loading">Loading…</div>;
    }

    const summary = view?.summary;
    const engagement = view?.engagement;

    return (
        <div className="max-w-3xl mx-auto px-6 py-10" data-testid="handover-page">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                <ArrowRightLeft size={14} /> Transition
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mt-2">Handover records tracker</h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl" data-testid="handover-framing">{checklist.framing}</p>
            <p className="text-xs text-slate-500 mt-2">{checklist.disclaimer}</p>
            <p className="text-xs text-amber-800 mt-3">
                Directors only. Do not publish named residents or unit arrears to the household pack.
            </p>

            {engagements.length > 1 && (
                <div className="mt-4">
                    <Label htmlFor="eng">Managing agent</Label>
                    <select
                        id="eng"
                        value={engagementId || ""}
                        onChange={(e) => setEngagementId(e.target.value)}
                        className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                        data-testid="handover-engagement-select"
                    >
                        {engagements.map((e) => (
                            <option key={e.id} value={e.id}>
                                {e.agency_name}{e.terminated_at ? ` (ended ${e.terminated_at})` : " (current)"}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {!engagementId && (
                <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5" data-testid="handover-no-engagement">
                    <p className="text-sm text-slate-700">
                        Record the managing agent first (name and end date) and the tracker can start. These are
                        the records an outgoing agent usually owes the management company:
                    </p>
                    <ol className="mt-3 space-y-1 text-sm text-slate-600 list-decimal list-inside">
                        {checklist.items.map((item) => (
                            <li key={item.key} data-testid={`handover-item-${item.key}`}>{item.label}</li>
                        ))}
                    </ol>
                </div>
            )}

            {engagementId && view && !view.started && (
                <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5" data-testid="handover-not-started">
                    <p className="text-sm text-slate-700">
                        Track {engagement?.agency_name ? <strong>{engagement.agency_name}</strong> : "this agent"}'s
                        handover item by item: what has been asked for, what has arrived, and what you have actually
                        accepted. These {checklist.items.length} records are the usual list:
                    </p>
                    <ol className="mt-3 space-y-1 text-sm text-slate-600 list-decimal list-inside">
                        {checklist.items.map((item) => (
                            <li key={item.key} data-testid={`handover-item-${item.key}`}>{item.label}</li>
                        ))}
                    </ol>
                    <Button onClick={start} disabled={busy} className="mt-4 bg-slate-900 text-white h-9 text-xs" data-testid="handover-start">
                        Start tracking
                    </Button>
                </div>
            )}

            {view && view.started && (
                <>
                    <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4" data-testid="handover-summary">
                        <div className="flex items-baseline justify-between gap-3 flex-wrap">
                            <div className="font-heading text-xl font-bold tabular-nums">
                                {summary.accepted} of {summary.applicable} accepted
                            </div>
                            <div className="text-xs uppercase tracking-wide text-slate-500" data-testid="handover-status">
                                Status: {engagement.handover_status.replace("_", " ")}
                            </div>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full bg-slate-700" style={{ width: `${summary.percent_accepted}%` }} />
                        </div>
                    </div>

                    <ol className="mt-4 space-y-2">
                        {view.items.map((item) => (
                            <li
                                key={item.id}
                                className="rounded-lg border border-slate-200 bg-white p-4 text-sm"
                                data-testid={`handover-item-${item.key}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-medium text-slate-900">{item.label}</div>
                                        <div className="mt-1 text-xs text-slate-500 space-x-3">
                                            {item.owner && <span>Owner: {item.owner}</span>}
                                            {item.requested_at && <span>Asked for {item.requested_at}</span>}
                                            {item.received_at && <span>Received {item.received_at}</span>}
                                            {item.due_date && <span>Diary date {item.due_date}</span>}
                                        </div>
                                        {item.evidence_document_ids.length > 0 && (
                                            <div className="mt-1 text-xs text-slate-600" data-testid={`handover-evidence-${item.key}`}>
                                                Evidence: {item.evidence_document_ids.map((id) => docTitle[id] || "a document").join(", ")}
                                            </div>
                                        )}
                                        {item.exception_reason && (
                                            <div className="mt-1 text-xs text-amber-800">
                                                Accepted without a document: {item.exception_reason}
                                            </div>
                                        )}
                                        {item.na_reason && (
                                            <div className="mt-1 text-xs text-slate-600">Does not apply: {item.na_reason}</div>
                                        )}
                                        {item.note && <div className="mt-1 text-xs text-slate-600">Note: {item.note}</div>}
                                    </div>
                                    <div className="text-right shrink-0">
                                        <span
                                            className={`inline-block rounded-full border px-2 py-0.5 text-[11px] ${STATE_TONE[item.state]}`}
                                            data-testid={`handover-state-${item.key}`}
                                        >
                                            {STATE_LABEL[item.state]}
                                        </span>
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => openEdit(item)}
                                                className="text-xs underline text-slate-700 mt-1"
                                                data-testid={`handover-edit-${item.key}`}
                                            >
                                                Update
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ol>
                </>
            )}

            <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
                <DialogContent data-testid="handover-dialog" className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing?.label}</DialogTitle>
                        <DialogDescription>
                            Currently: {editing ? STATE_LABEL[editing.state] : ""}. Record what has actually happened;
                            dates are the ones you enter.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3">
                        <div>
                            <Label htmlFor="h-state">Move to</Label>
                            <select
                                id="h-state"
                                value={edit.state}
                                onChange={(e) => setEdit({ ...edit, state: e.target.value, date: today() })}
                                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                                data-testid="handover-state-select"
                            >
                                <option value="">No change — just update the details</option>
                                {(editing?.allowed_next || []).map((s) => (
                                    <option key={s} value={s}>{STATE_LABEL[s]}</option>
                                ))}
                            </select>
                        </div>

                        {needsDate && (
                            <div>
                                <Label htmlFor="h-date">
                                    {edit.state === "requested" ? "Date you asked for it" : "Date it arrived"}
                                </Label>
                                <Input id="h-date" type="date" max={today()} value={edit.date}
                                    onChange={(e) => setEdit({ ...edit, date: e.target.value })} data-testid="handover-date" />
                            </div>
                        )}

                        {edit.state === "needs_correction" && (
                            <div>
                                <Label htmlFor="h-note">What needs correcting</Label>
                                <Textarea id="h-note" value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })}
                                    data-testid="handover-correction-note" />
                            </div>
                        )}

                        {edit.state === "not_applicable" && (
                            <div>
                                <Label htmlFor="h-na">Why this does not apply</Label>
                                <Textarea id="h-na" value={edit.na_reason} onChange={(e) => setEdit({ ...edit, na_reason: e.target.value })}
                                    data-testid="handover-na-reason" />
                            </div>
                        )}

                        {showEvidence && (
                            <div data-testid="handover-evidence-picker">
                                <Label>Evidence documents</Label>
                                <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 p-2 space-y-1">
                                    {docs.length === 0 && <p className="text-xs text-slate-500">No documents uploaded yet.</p>}
                                    {docs.map((d) => (
                                        <label key={d.id} className="flex items-center gap-2 text-sm">
                                            <input type="checkbox" checked={edit.evidence.includes(d.id)} onChange={() => toggleEvidence(d.id)} />
                                            {d.title}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {edit.state === "accepted" && edit.evidence.length === 0 && (
                            <div>
                                <Label htmlFor="h-exc">No document? Say why you are accepting it anyway</Label>
                                <Textarea id="h-exc" value={edit.exception_reason}
                                    onChange={(e) => setEdit({ ...edit, exception_reason: e.target.value })}
                                    data-testid="handover-exception" />
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="h-owner">Owner (who is on it)</Label>
                                <Input id="h-owner" value={edit.owner} onChange={(e) => setEdit({ ...edit, owner: e.target.value })} />
                            </div>
                            <div>
                                <Label htmlFor="h-due">Diary date (a reminder, not a deadline)</Label>
                                <Input id="h-due" type="date" value={edit.due_date} onChange={(e) => setEdit({ ...edit, due_date: e.target.value })} />
                            </div>
                        </div>

                        {!edit.state && (
                            <div>
                                <Label htmlFor="h-note2">Note</Label>
                                <Textarea id="h-note2" value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} />
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                        <Button type="button" onClick={save} disabled={busy} className="bg-slate-900 text-white" data-testid="handover-save">
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
